import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, StudentError } from "../studentClient.js";
import { badgeClass } from "../../admin/badge.js";

interface Me {
  fullName: string;
  email: string;
  courseName: string;
  status: string;
  enrolledAt: string;
  batch?: { name: string; startDate: string; endDate: string; status: string };
  idph?: { status: string; attempts?: { attemptNumber: number; result: string; date: string }[] };
  payment?: { status: string; amountDue?: number; amountPaid?: number };
}

const money = (n?: number) => (n == null ? "-" : `$${n.toLocaleString()}`);

const LINKS = [
  { to: "/portal/homework", ic: "h", name: "Homework", cap: "This week's work", icon: <path d="M12 16V4M7 9l5-5 5 5M5 20h14" /> },
  { to: "/portal/quizzes", ic: "q", name: "Quizzes", cap: "Weekly quiz", icon: <><path d="M9 11l3 3 8-8" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></> },
  { to: "/portal/welcome", ic: "w", name: "Welcome", cap: "Handbook & forms", icon: <><path d="M20 12v9H4v-9" /><path d="M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></> },
];

export default function DashboardPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api<{ student: Me }>("/student/me")
      .then((d) => setMe(d.student))
      .catch((e) => setError(e instanceof StudentError ? e.message : "Failed to load your dashboard"));
  }, []);

  if (error) return <div className="login-err">{error}</div>;
  if (!me) return <p className="subhead">Loading...</p>;

  const first = (me.fullName || "").trim().split(/\s+/)[0] || "there";
  const paid = me.payment?.amountPaid;
  const due = me.payment?.amountDue;
  const settled = due === 0;

  return (
    <>
      <section className="wel-hero">
        <div className="dash-herotop">
          <span className="wel-kicker">Student portal</span>
          <span className="dash-statuschip">{me.status}</span>
        </div>
        <h1>Hi, {first}</h1>
        <p>
          You're enrolled in <strong style={{ color: "var(--coral-soft)" }}>{me.courseName}</strong>
          {me.batch ? <> - {me.batch.name}.</> : "."} Everything for your program lives here.
        </p>
      </section>

      <div className="dash-links">
        {LINKS.map((l) => (
          <Link key={l.to} to={l.to} className="dash-tile">
            <span className={`ic ${l.ic}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{l.icon}</svg></span>
            <span className="dt-text"><span className="dt-name">{l.name}</span><span className="dt-cap">{l.cap}</span></span>
            <svg className="go" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </Link>
        ))}
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="alabel" style={{ marginTop: 0 }}>Enrollment</div>
        <dl className="kv">
          <dt>Program</dt><dd>{me.courseName}</dd>
          <dt>Batch</dt><dd>{me.batch ? me.batch.name : "-"}</dd>
          {me.batch && <><dt>Dates</dt><dd>{me.batch.startDate} → {me.batch.endDate}</dd></>}
          {me.batch && <><dt>Batch status</dt><dd><span className={badgeClass(me.batch.status)}>{me.batch.status}</span></dd></>}
          <dt>Enrolled</dt><dd>{new Date(me.enrolledAt).toLocaleDateString()}</dd>
        </dl>
      </div>

      {me.payment && (
        <div className="card pad" style={{ marginBottom: 16 }}>
          <div className="row" style={{ marginBottom: 10 }}>
            <div className="alabel" style={{ marginTop: 0 }}>Payment</div>
            <span className={badgeClass(me.payment.status)}>{me.payment.status}</span>
          </div>
          <div className="paystats">
            <div className="paystat paid"><div className="lbl">Received</div><div className="val">{money(paid ?? 0)}</div></div>
            <div className={`paystat due${settled ? " clear" : ""}`}>
              <div className="lbl">Balance due</div>
              <div className="val">{settled ? "Paid in full" : money(due)}</div>
            </div>
          </div>
        </div>
      )}

      {me.idph && (
        <div className="card pad">
          <div className="row"><div className="alabel" style={{ marginTop: 0 }}>IDPH status</div><span className={badgeClass(me.idph.status)}>{me.idph.status}</span></div>
          <p className="sub" style={{ marginTop: 6, marginBottom: 0 }}>Exam attempts recorded: {me.idph.attempts?.length ?? 0}</p>
        </div>
      )}
    </>
  );
}
